import { t } from "@web-monorepo/i18n/macro";
import isMobile from "@web-monorepo/utilities/src/mobile";
import { DDSIconButton, DDSLabelText } from "@web-monorepo/dds";
import { IconX } from "@web-monorepo/dds-icons";
import { useOnFirstRender } from "@web-monorepo/hooks";
import type { TranslatedString } from "@web-monorepo/translation-runtime";
import { clsx } from "clsx";
import FocusTrap from "focus-trap-react";
import ExperiencesContainer from "src/pods/ideas/components/experiences/ExperiencesContainer";
import { useLaunchpadAppNavigation } from "src/pods/launchpad/useLaunchpadAppNavigation";
import type { AppPageVariant } from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";
import { metrics } from "src/utils/metrics";
import styles from "./Launchpad.module.css";
import WelcomeCallout from "./WelcomeCallout";

type LaunchpadProps = {
  apps: {
    href: string;
    title: TranslatedString;
    /** Drives the icon-tile brand color via the CSS Module's variant rules. */
    variant: AppPageVariant;
    icon?: string;
  }[];
  classId: string;
  mountPoint: string;
  remoteClose: () => void;
  dismissWelcomeNux?: () => void;
  shouldRenderWelcome?: boolean;
};

export default function Launchpad({
  shouldRenderWelcome,
  dismissWelcomeNux,
  apps,
  classId,
  mountPoint,
  remoteClose,
}: LaunchpadProps) {
  useOnFirstRender(() => {
    metrics.increment("main.open");
    logEvent({ eventName: "main.open" });
  });

  const navigateToApp = useLaunchpadAppNavigation();

  return (
    <FocusTrap
      active={!isMobile.any}
      focusTrapOptions={{
        fallbackFocus: "#toolkitLaunchpad",
        allowOutsideClick: true,
      }}
    >
      <div className={styles.page} id="toolkitLaunchpad" tabIndex={-1}>
        {mountPoint === "teach" && (
          <DDSIconButton
            data-name="launchpad:close_button"
            aria-label={t`Close toolkit`}
            tooltip={{ content: t`Close toolkit`, dataName: "launchpad:close_button:tooltip" }}
            variant="transparent"
            className={styles.closeButton}
            onClick={remoteClose}
          >
            <IconX />
          </DDSIconButton>
        )}
        {mountPoint === "mobile" && shouldRenderWelcome && (
          <section className={styles.calloutSection}>
            <WelcomeCallout dismissWelcomeNux={dismissWelcomeNux} />
          </section>
        )}
        <section className={styles.appsSection}>
          {/* role="list" restores VoiceOver list semantics that `list-style: none` strips —
              https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver workaround */}
          <ul role="list" className={styles.grid}>
            {apps.map(({ title, icon, variant, href }) => (
              <li key={title} className={styles.gridItem}>
                {/* Keep the raw button's icon-tile/label stack and custom focus styling during this
                    translation-only migration. Metadata titles are already localized. */}
                <button
                  type="button"
                  data-name="launchpad:launchpad_app_icon"
                  aria-label={title}
                  onClick={() => navigateToApp(href)}
                  className={styles.appButton}
                >
                  <div className={clsx(styles.iconTile, styles[variant])}>
                    <img src={icon} alt="" />
                  </div>
                  <DDSLabelText className={styles.appLabel}>{title}</DDSLabelText>
                </button>
              </li>
            ))}
          </ul>
        </section>
        {mountPoint === "mobile" && <ExperiencesContainer classId={classId} mountPoint={mountPoint} />}
      </div>
    </FocusTrap>
  );
}
